import { Form, type FormItemProps } from 'antd';
import { set as lodashSet, template } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import type { FieldError } from 'react-hook-form';
import {
  useController,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';

export function getErrorTip(
  name: string,
  label: ReactNode,
  error: FieldError | undefined,
) {
  if (!error) {
    return undefined;
  }

  if (error.root) {
    return getErrorTip(name, label, error.root);
  }

  if (!error.message) {
    return undefined;
  }

  const data = {};
  lodashSet(data, name, typeof label === 'string' ? label : '');

  try {
    // 如果校验规则中未设置 label，则使用 Form.Item 的 label
    return template(error.message)(data);
  } catch {
    // 一些场景下，嵌套字段可能为 undefined，似乎 template 不会使用 get 自动处理字段为空的情况
    // 而导致这一场景的原因是 react-hook-form 在使用 useFieldArray 的 remove 方法的时候不会立即自动更新错误信息，导致实际控件的 index 和错误信息的 index 错乱
    // 此时相应位置上获取的还是之前的 index
    return error.message;
  }
}

export interface FormControlProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>
  extends
    Pick<
      FormItemProps,
      | 'label'
      | 'required'
      | 'tooltip'
      | 'layout'
      | 'labelCol'
      | 'wrapperCol'
      | 'className'
      | 'rootClassName'
      | 'extra'
    >,
    ControllerProps<TFieldValues, TName, TTransformedValues> {}

export function FormControl<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>(props: FormControlProps<TFieldValues, TName, TTransformedValues>) {
  const {
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
    render,
    exact,
    disabled,
    ...formItemProps
  } = props;

  const controller = useController({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
    exact,
    disabled,
  });

  const { fieldState } = controller;

  const validateStatus = useMemo<FormItemProps['validateStatus']>(() => {
    if (fieldState.invalid) {
      return 'error';
    }

    return;
  }, [fieldState]);

  const isError = useMemo(() => validateStatus === 'error', [validateStatus]);

  const help = useMemo(() => {
    return isError
      ? getErrorTip(name, formItemProps.label, fieldState.error)
      : void 0;
  }, [isError, name, formItemProps.label, fieldState.error]);

  return (
    <Form.Item
      status="error"
      validateStatus={validateStatus}
      help={help}
      {...formItemProps}
    >
      {render(controller)}
    </Form.Item>
  );
}
